import { motion } from 'framer-motion';
import { Trophy } from 'lucide-react';
import { getEarnedBadgesDetails, ACHIEVEMENT_BADGES } from '../../services/gamificationService';
import { calculateTenure } from '../TenureBadge/TenureBadge';
import { ServiceTile, Medallion } from '../awards/AwardBadge';
import { SERVICE_AWARDS } from '../awards/serviceAwards';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { parseHireDate } from '../../utils/hireDate';

// Matches the grids' own `sm:` breakpoint — the tile actually rendered in the
// grid's last CSS column must be the one whose tooltip aligns 'end', or the
// fixed-width tooltip sails past the card into whatever sits beside it.
const DESKTOP_COLS_QUERY = '(min-width: 640px)';

function tooltipAlignFor(index, mobileCols, desktopCols, isDesktop) {
  const cols = isDesktop ? desktopCols : mobileCols;
  const col = index % cols;
  if (col === 0) return 'start';
  if (col === cols - 1) return 'end';
  return 'center';
}

/**
 * Trophy case — the destination for the badges pulled out of the directory row.
 *
 * Three things were wrong with the old showcase, all of them audit findings:
 *
 * 1. It rendered only EARNED badges, so there was no way to learn what was
 *    earnable. Empty categories showed a padlock and "No badges earned yet",
 *    which told you nothing about what would fill them.
 * 2. It counted all eight time-gated tenure tiers in a "n / 22 earned"
 *    fraction, so a three-year employee read it as failing at eighteen things
 *    when six of them are pure waiting.
 * 3. Service milestones and achievements rendered identically — a 25-year
 *    service crown looked like "you uploaded a photo".
 *
 * So: two clearly separate sections. Service is a ladder with a progression and
 * a dated next rung. Achievements are a fraction, and the unearned ones are
 * visible so they can actually be pursued.
 */

const TENURE_LADDER = SERVICE_AWARDS;

function SectionLabel({ children, aside }) {
  return (
    <div className="flex items-baseline justify-between gap-3 mb-2">
      <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-ink-4">{children}</p>
      {aside && <p className="text-[11px] text-ink-3">{aside}</p>}
    </div>
  );
}

function getBadgeTooltip(badge) {
  if (badge.category === 'tenure') {
    if (badge.years === 0) return 'Awarded automatically once a hire date is on your record.';
    return `Awarded automatically when you reach ${badge.years} ${badge.years === 1 ? 'year' : 'years'} of service.`;
  }

  switch (badge.id) {
    case 'first_bio':
      return 'Earn this by adding a bio to your profile.';
    case 'first_skills':
      return 'Earn this by adding at least five expertise items to your profile.';
    case 'profile_complete':
      return 'Earn this by adding a bio, at least five expertise items, and enough extra profile detail to make your profile genuinely useful.';
    case 'custom_banner':
      return 'Earn this by customizing your profile banner.';
    case 'connector':
      return 'Earn this by turning on at least two collaboration preferences: speaking, white papers, or mentoring.';
    case 'cross_pollinator':
      return 'Earn this by adding at least one expertise tag, one skill, and one software/program skill.';
    case 'mentor':
      return 'Earn this by turning on mentoring and listing mentor topics.';
    case 'speaker_circuit':
      return 'Earn this by opening yourself to speaking and adding at least five expertise items.';
    case 'project_showcase':
      return 'Earn this by adding five MOREgroup projects to your profile.';
    case 'knowledge_base':
      return 'Earn this by adding a bio, five expertise items, three software/program skills, and at least one project.';
    default:
      return badge.description;
  }
}

/**
 * One tile. `state` is 'earned' | 'next' | 'locked'.
 * Locked is dashed and unfilled with no padlock — a padlock reads as "forbidden",
 * and these are simply not yet reached.
 *
 * The award object itself comes in as `visual`, from awards/AwardBadge, so a rung
 * here is the same object as the one in the earn moment. It used to be an emoji in
 * a grey circle, greyscaled when locked — which made every unearned rung look like
 * a rendering failure, and made a 30-year crest and a 2-year tile the same picture.
 */
function Tile({ badge, state, visual, onClick, subtitle, tooltipAlign = 'center' }) {
  const base =
    'group relative flex flex-col items-center justify-center p-2 rounded-ctl border transition-all text-center';
  const style =
    state === 'earned'
      ? 'bg-surface border-line hover:border-accent-line hover:shadow-e1'
      : state === 'next'
        ? 'bg-transparent border-dashed border-accent-line'
        : 'bg-transparent border-dashed border-line';

  const clickable = state === 'earned' && Boolean(onClick);
  const tooltip = getBadgeTooltip(badge);
  const tooltipPositionClass =
    tooltipAlign === 'start'
      ? 'left-0 translate-x-0'
      : tooltipAlign === 'end'
        ? 'right-0 translate-x-0'
        : 'left-1/2 -translate-x-1/2';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.18 }}
      onClick={clickable ? () => onClick(badge) : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(badge);
        }
      } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={`${badge.name}. ${tooltip}`}
      className={`${base} ${style} ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
    >
      {visual}
      <span className={`mt-1 text-[9.5px] font-semibold leading-tight line-clamp-2
        ${state === 'earned' ? 'text-ink-2' : 'text-ink-4'}`}>
        {badge.name}
      </span>
      {subtitle && (
        <span className="text-[9px] text-accent-ink font-semibold mt-0.5">{subtitle}</span>
      )}
      <div
        className={`pointer-events-none absolute bottom-full z-20 mb-2 w-44 rounded-lg
                   border border-line bg-surface px-2.5 py-2 text-[10.5px] leading-snug text-ink-2 shadow-e2
                   opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100
                   ${tooltipPositionClass}`}
      >
        {tooltip}
      </div>
    </motion.div>
  );
}

export function BadgeShowcase({ userId, hireDate, onBadgeClick }) {
  const isDesktopCols = useMediaQuery(DESKTOP_COLS_QUERY);
  const years = calculateTenure(hireDate);

  const earnedAchievements = getEarnedBadgesDetails(userId);
  const earnedIds = new Set(earnedAchievements.map((b) => b.id));
  const allAchievements = Object.values(ACHIEVEMENT_BADGES);

  const currentRung = [...TENURE_LADDER].reverse().find((rung) => years >= rung.years);
  const nextRung = TENURE_LADDER.find((rung) => rung.years > years);

  const nextDate = (() => {
    if (!nextRung || !hireDate) return null;
    const at = parseHireDate(hireDate);
    if (!at) return null;
    at.setFullYear(at.getFullYear() + nextRung.years);
    return at.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  })();

  const progression = hireDate
    ? nextRung
      ? `${currentRung?.name} · next at ${nextRung.years} years${nextDate ? ` (${nextDate})` : ''}`
      : `${currentRung?.name} · top of the ladder`
    : 'Hire date not available';

  return (
    <div className="space-y-4">
      <h3 className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-ink-4 flex items-center gap-1.5">
        <Trophy size={12} />
        Trophy case
      </h3>

      {/* Service — a ladder, never a fraction: the rungs are reached by waiting,
          so counting them as things you failed to collect is nonsense. */}
      <div>
        <SectionLabel aside={progression}>Service</SectionLabel>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
          {TENURE_LADDER.map((rung, index) => {
            const earned = years >= rung.years;
            const isNext = nextRung?.tier === rung.tier;
            const tooltipAlign = tooltipAlignFor(index, 4, 8, isDesktopCols);
            return (
              <Tile
                key={rung.tier}
                badge={{ ...rung, category: 'tenure' }}
                state={earned ? 'earned' : isNext ? 'next' : 'locked'}
                visual={(
                  <ServiceTile
                    years={rung.years}
                    size={36}
                    state={earned ? 'earned' : isNext ? 'next' : 'locked'}
                  />
                )}
                onClick={onBadgeClick}
                subtitle={isNext ? `${rung.years}y` : null}
                tooltipAlign={tooltipAlign}
              />
            );
          })}
        </div>
      </div>

      {/* Achievements — a real fraction, and the unearned ones are shown so they
          can be pursued. Previously only earned badges rendered, so there was no
          way to discover what was earnable. */}
      <div>
        <SectionLabel
          aside={
            <>
              <b className="font-mono font-semibold text-ink-2">{earnedAchievements.length}</b>
              {' of '}
              <b className="font-mono font-semibold text-ink-2">{allAchievements.length}</b>
              {' earned'}
            </>
          }
        >
          Achievements
        </SectionLabel>
        <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5">
          {allAchievements.map((badge, index) => {
            const earned = earnedIds.has(badge.id);
            const detail = earnedAchievements.find((b) => b.id === badge.id);
            const tooltipAlign = tooltipAlignFor(index, 4, 5, isDesktopCols);
            return (
              <Tile
                key={badge.id}
                badge={detail || badge}
                state={earned ? 'earned' : 'locked'}
                visual={(
                  <Medallion size={36} metal="brand" state={earned ? 'earned' : 'locked'}>
                    {badge.icon}
                  </Medallion>
                )}
                onClick={onBadgeClick}
                tooltipAlign={tooltipAlign}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
